import { theme, useResponsive } from "@product/scmp-sdk";
import type { EmbeddedSpeechifyExperience } from "https://storage.googleapis.com/speechify-api-cdn/speechifyapi.min.mjs";
import { useAtom, useAtomValue } from "jotai";
import type { FunctionComponent } from "react";
import { useEffect, useRef, useState } from "react";

import { SpeechSkipClassName } from "~/components/article/article-speech/consts";
import { scmpYoutubePlayerAtom } from "~/components/scmp-youtube-video/scmp-youtube-video-player/atoms";
import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import { rosettaAtom } from "~/lib/rosetta";
import { ZIndexLayer } from "~/lib/styles";

import { articleSpeechPlayerAtom } from "./atoms";
import { Container } from "./styles";

type Props = {
  articleElementReference: HTMLElement | null;
};

export const ArticleSpeechPlayer: FunctionComponent<Props> = ({ articleElementReference }) => {
  const reference = useRef<HTMLDivElement>(null);
  const [isMounted, setIsMounted] = useState(false);
  const [isInlinePlayerShown, setIsInlinePlayerShown] = useState(false);

  const asyncRosettaState = useAtomValue(rosettaAtom);

  const isMobile = useResponsive(theme.breakpoints.only("mobile"), false);

  const [{ articleSpeechPlayer }, setArticleSpeechPlayerAtom] = useAtom(articleSpeechPlayerAtom);
  const { scmpYoutubePlayer } = useAtomValue(scmpYoutubePlayerAtom);

  useEffect(() => {
    if (
      !reference.current ||
      !articleElementReference ||
      isMounted ||
      !asyncRosettaState ||
      asyncRosettaState.status !== "success"
    )
      return;

    const shouldShowPaywall =
      asyncRosettaState.status === "success" && !asyncRosettaState.result?.hasPostiesAccessRight;

    if (shouldShowPaywall) return;

    let widget: EmbeddedSpeechifyExperience;

    const run = async () => {
      const speechifyWidget = await import(
        /* webpackIgnore: true */ "https://storage.googleapis.com/speechify-api-cdn/speechifyapi.min.mjs"
      );
      widget = speechifyWidget.makeSpeechifyExperience({
        customStyles: {
          playButton: {
            background: "radial-gradient(#ffffff 50%, transparent 50%)",
          },
          primaryColor: "#ff7c1d",
          widget: {
            borderRadius: "8px",
            zIndex: `${ZIndexLayer.Drawer - 1}`,
          },
        },
        ignoreElements(element) {
          return (
            element.tagName === "NOSCRIPT" ||
            element.tagName === "STYLE" ||
            element.tagName === "SCRIPT" ||
            element.classList.contains(SpeechSkipClassName)
          );
        },
        inlinePlayerElement:
          reference?.current === null || !isMobile ? undefined : reference.current,
        paragraphSettings: {
          isEnabled: "while-playing",
          marginRight: "-10px",
        },
        rootElement: articleElementReference,
        visibility: {
          showWidget: !isMobile,
          showWidgetOnPlay: !isMobile,
        },
      });

      let lastSeenPercentage = 0;
      widget.addEventListener((event, playerState) => {
        switch (event.type) {
          case "inline-player-shown":
            setIsInlinePlayerShown(true);
            break;
          case "pause":
            setArticleSpeechPlayerAtom(articleSpeechPlayerAtom => ({
              ...articleSpeechPlayerAtom,
              isArticleSpeechPlayerPlaying: false,
            }));
            sendGATracking({
              action: "TTS/Pause",
              category: "TTS",
              label: `${playerState?.progressFraction} - ${playerState?.audioLengthSeconds}`,
            });
            break;
          case "play":
            setArticleSpeechPlayerAtom(articleSpeechPlayerAtom => ({
              ...articleSpeechPlayerAtom,
              isArticleSpeechPlayerPlaying: true,
            }));
            sendGATracking({
              action: "TTS/Play",
              category: "TTS",
              label: `${playerState?.progressFraction} - ${playerState?.audioLengthSeconds}`,
            });
            break;
          case "progress":
            const percentageElapsed = Math.round((playerState?.progressFraction ?? 0) * 100);
            if (
              percentageElapsed > 0 &&
              percentageElapsed % 10 === 0 &&
              percentageElapsed !== lastSeenPercentage &&
              Math.abs(percentageElapsed - lastSeenPercentage) >= 10
            ) {
              sendGATracking({
                action: "TTS/Progress",
                category: "TTS",
                label: `${playerState?.progressFraction} - ${playerState?.audioLengthSeconds}`,
              });
              lastSeenPercentage = percentageElapsed;
            }
            break;
          case "progress-finished":
            sendGATracking({
              action: "TTS/Progress Finished",
              category: "TTS",
              label: `${playerState?.progressFraction} - ${playerState?.audioLengthSeconds}`,
            });
            break;
          case "resume":
            setArticleSpeechPlayerAtom(articleSpeechPlayerAtom => ({
              ...articleSpeechPlayerAtom,
              isArticleSpeechPlayerPlaying: true,
            }));
            sendGATracking({
              action: "TTS/Resume",
              category: "TTS",
              label: `${playerState?.progressFraction} - ${playerState?.audioLengthSeconds}`,
            });
            break;
        }
      });

      await widget.mount();
      setArticleSpeechPlayerAtom(articleSpeechPlayerAtom => ({
        ...articleSpeechPlayerAtom,
        articleSpeechPlayer: widget,
      }));
    };
    setIsMounted(true);
    void run();
  }, [
    articleElementReference,
    asyncRosettaState,
    isMobile,
    isMounted,
    reference,
    setArticleSpeechPlayerAtom,
  ]);

  useEffect(() => {
    if (!articleSpeechPlayer || !scmpYoutubePlayer) {
      return;
    }

    articleSpeechPlayer.addEventListener(event => {
      switch (event.type) {
        case "play":
        case "resume":
          void scmpYoutubePlayer.pauseVideo();
          break;
      }
    });
  }, [articleSpeechPlayer, scmpYoutubePlayer]);

  useEffect(() => {
    if (!articleSpeechPlayer) {
      return;
    }

    return () => {
      void articleSpeechPlayer.unmount();
    };
  }, [articleSpeechPlayer]);

  return <Container $hasPlaceholder={!isInlinePlayerShown} ref={reference} />;
};
